import { useEffect, useRef } from "react";

// GIRDER PANIC — a mobile Donkey Kong-style climber
// Original characters/theme (no Nintendo assets). Canvas game + DOM touch controls.

const GW = 360;
const GH = 560;

// Platforms: index 0 = floor, higher index = higher tier. y = top surface.
const PLATFORMS = [
  { x: 0, y: 520, w: 360 },   // 0 floor  (barrels roll LEFT, despawn)
  { x: 0, y: 440, w: 330 },   // 1 (barrels roll RIGHT, fall at right end)
  { x: 30, y: 360, w: 330 },  // 2 (LEFT)
  { x: 0, y: 280, w: 330 },   // 3 (RIGHT)
  { x: 30, y: 200, w: 330 },  // 4 (LEFT)
  { x: 0, y: 120, w: 330 },   // 5 (RIGHT — spawn tier)
  { x: 120, y: 60, w: 120 },  // 6 goal platform
];
const barrelDir = (i) => ((5 - i) % 2 === 0 ? 1 : -1);

// Ladders: x center, yTop (upper platform surface), yBot (lower platform surface)
const LADDERS = [
  { x: 310, yTop: 440, yBot: 520 },
  { x: 150, yTop: 440, yBot: 520 },
  { x: 46, yTop: 360, yBot: 440 },
  { x: 220, yTop: 360, yBot: 440 },
  { x: 310, yTop: 280, yBot: 360 },
  { x: 120, yTop: 280, yBot: 360 },
  { x: 46, yTop: 200, yBot: 280 },
  { x: 250, yTop: 200, yBot: 280 },
  { x: 310, yTop: 120, yBot: 200 },
  { x: 100, yTop: 120, yBot: 200 },
  { x: 180, yTop: 60, yBot: 120 }, // final ladder to the goal
];

const COLORS = {
  bg: "#140f26",
  girder: "#f2a03d",
  girderDark: "#a85f14",
  ladder: "#4fd8d0",
  player: "#ff5a5a",
  playerSuit: "#3a6cff",
  barrel: "#d97b2c",
  barrelDark: "#7a3d10",
  krank: "#9b6cff",
  goal: "#7dff8a",
  text: "#fdf6e3",
};

export default function GirderPanic() {
  const canvasRef = useRef(null);
  const keys = useRef({ left: false, right: false, up: false, down: false, jump: false });
  const jumpLatch = useRef(false);
  const S = useRef(null);

  const freshPlayer = () => ({
    x: 30, y: 520, vy: 0, w: 18, h: 24,
    climbing: false, grounded: true, facing: 1, anim: 0,
  });

  const freshState = () => ({
    screen: "title", // title | playing | dead | levelup | gameover
    score: 0, hi: 0, lives: 3, level: 1,
    player: freshPlayer(),
    barrels: [],
    spawnTimer: 90,
    krankAnim: 0,
    texts: [],
    timer: 0,     // frames in level (bonus countdown)
    stateTimer: 0,
    tick: 0,
  });

  useEffect(() => {
    S.current = freshState();
    const ctx = canvasRef.current.getContext("2d");
    let raf;

    const onKey = (e, downFlag) => {
      const map = {
        ArrowLeft: "left", a: "left", ArrowRight: "right", d: "right",
        ArrowUp: "up", w: "up", ArrowDown: "down", s: "down",
        " ": "jump", Spacebar: "jump",
      };
      const k = map[e.key];
      if (k) { keys.current[k] = downFlag; e.preventDefault(); }
    };
    const kd = (e) => onKey(e, true);
    const ku = (e) => onKey(e, false);
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);

    const spawnInterval = (lv) => Math.max(50, 120 - lv * 12);
    const barrelSpeed = (lv) => 1.5 + lv * 0.25;

    function platformUnder(x, y, tol = 6) {
      for (let i = PLATFORMS.length - 1; i >= 0; i--) {
        const p = PLATFORMS[i];
        if (x >= p.x - 2 && x <= p.x + p.w + 2 && y >= p.y - tol && y <= p.y + tol) return i;
      }
      return -1;
    }

    function step() {
      const st = S.current;
      st.tick++;

      if (st.screen !== "playing") {
        st.stateTimer--;
        // start / restart on jump press
        if ((st.screen === "title" || st.screen === "gameover") && keys.current.jump && !jumpLatch.current) {
          jumpLatch.current = true;
          const hi = Math.max(st.hi, st.score);
          S.current = { ...freshState(), screen: "playing", hi };
        } else if (st.screen === "dead" && st.stateTimer <= 0) {
          st.player = freshPlayer();
          st.barrels = [];
          st.spawnTimer = 60;
          st.screen = "playing";
        } else if (st.screen === "levelup" && st.stateTimer <= 0) {
          st.level++;
          st.player = freshPlayer();
          st.barrels = [];
          st.timer = 0;
          st.spawnTimer = 60;
          st.screen = "playing";
        }
        if (!keys.current.jump) jumpLatch.current = false;
        return;
      }

      st.timer++;
      const P = st.player;
      const k = keys.current;

      // ---- ladder logic ----
      if (!P.climbing && (k.up || k.down)) {
        for (const L of LADDERS) {
          const near = Math.abs(P.x - L.x) < 12;
          if (!near) continue;
          const inSpan = P.y > L.yTop + 4 && P.y <= L.yBot + 4;
          const atTop = Math.abs(P.y - L.yTop) < 6 && k.down;
          if ((k.up && inSpan) || atTop) {
            P.climbing = true; P.ladder = L; P.x = L.x; P.vy = 0;
            break;
          }
        }
      }

      if (P.climbing) {
        const L = P.ladder;
        if (k.up) { P.y -= 1.8; P.anim++; }
        if (k.down) { P.y += 1.8; P.anim++; }
        if (P.y <= L.yTop) { P.y = L.yTop; P.climbing = false; P.grounded = true; }
        if (P.y >= L.yBot) { P.y = L.yBot; P.climbing = false; P.grounded = true; }
      } else {
        // ---- walking / jumping ----
        if (k.left) { P.x -= 2.2; P.facing = -1; P.anim++; }
        if (k.right) { P.x += 2.2; P.facing = 1; P.anim++; }
        P.x = Math.max(9, Math.min(GW - 9, P.x));

        if (k.jump && !jumpLatch.current && P.grounded) {
          P.vy = -7.6; P.grounded = false; jumpLatch.current = true;
        }
        if (!k.jump) jumpLatch.current = false;

        P.vy += 0.45;
        P.y += P.vy;
        P.grounded = false;
        if (P.vy >= 0) {
          const pi = platformUnder(P.x, P.y, 8);
          if (pi >= 0) { P.y = PLATFORMS[pi].y; P.vy = 0; P.grounded = true; }
        }
        if (P.y > GH + 40) hitPlayer(st); // fell off (shouldn't happen, but safe)
      }

      // ---- reach the goal ----
      const goal = PLATFORMS[6];
      if (P.y <= goal.y + 4 && P.x > goal.x && P.x < goal.x + goal.w) {
        const bonus = Math.max(0, 3000 - Math.floor(st.timer / 60) * 100);
        st.score += 1000 + bonus;
        st.texts.push({ x: P.x, y: P.y - 40, txt: `+${1000 + bonus}`, t: 90 });
        st.screen = "levelup"; st.stateTimer = 140;
        return;
      }

      // ---- spawn barrels ----
      st.spawnTimer--;
      st.krankAnim = Math.max(0, st.krankAnim - 1);
      if (st.spawnTimer <= 0) {
        st.spawnTimer = spawnInterval(st.level);
        st.krankAnim = 20;
        st.barrels.push({
          x: 56, y: 120, vx: barrelSpeed(st.level), vy: 0,
          climbing: false, scored: false, roll: 0,
        });
      }

      // ---- barrels ----
      for (const b of st.barrels) {
        b.roll += b.vx;
        if (b.climbing) {
          b.y += 1.6;
          if (b.y >= b.targetY) {
            b.y = b.targetY; b.climbing = false;
            const pi = platformUnder(b.x, b.y, 6);
            b.vx = barrelDir(pi >= 0 ? pi : 0) * barrelSpeed(st.level);
          }
        } else {
          b.x += b.vx;
          b.vy += 0.45;
          b.y += b.vy;
          if (b.vy >= 0) {
            const pi = platformUnder(b.x, b.y, 8);
            if (pi >= 0) {
              b.y = PLATFORMS[pi].y; b.vy = 0;
              b.vx = barrelDir(pi) * barrelSpeed(st.level);
              // chance to take a ladder down
              if (Math.random() < 0.25) {
                for (const L of LADDERS) {
                  if (Math.abs(L.yTop - b.y) < 2 && Math.abs(L.x - b.x) < 10) {
                    b.climbing = true; b.x = L.x; b.targetY = L.yBot; b.vy = 0;
                    break;
                  }
                }
              }
            }
          }
        }

        // score a clean jump over a barrel
        if (!b.scored && !P.grounded && !P.climbing &&
            Math.abs(b.x - P.x) < 12 && P.y < b.y - 18 && b.y - P.y < 70) {
          b.scored = true;
          st.score += 100;
          st.texts.push({ x: b.x, y: b.y - 30, txt: "+100", t: 60 });
        }

        // collision (slightly forgiving hitboxes)
        const px1 = P.x - 7, px2 = P.x + 7, py1 = P.y - 22, py2 = P.y - 1;
        const bx1 = b.x - 6, bx2 = b.x + 6, by1 = b.y - 13, by2 = b.y - 1;
        if (px1 < bx2 && px2 > bx1 && py1 < by2 && py2 > by1) {
          hitPlayer(st);
          break;
        }
      }
      st.barrels = st.barrels.filter((b) => b.x > -20 && b.x < GW + 20 && b.y < GH + 40);

      st.texts.forEach((t) => { t.y -= 0.5; t.t--; });
      st.texts = st.texts.filter((t) => t.t > 0);
    }

    function hitPlayer(st) {
      st.lives--;
      if (st.lives <= 0) {
        st.hi = Math.max(st.hi, st.score);
        st.screen = "gameover"; st.stateTimer = 30;
      } else {
        st.screen = "dead"; st.stateTimer = 80;
      }
    }

    // ================= drawing =================
    function draw() {
      const st = S.current;
      ctx.fillStyle = COLORS.bg;
      ctx.fillRect(0, 0, GW, GH);

      // faint stars
      ctx.fillStyle = "rgba(255,255,255,0.10)";
      for (let i = 0; i < 30; i++) {
        ctx.fillRect(((i * 97) % GW), ((i * 53) % GH), 2, 2);
      }

      // ladders (behind girders' top edge)
      for (const L of LADDERS) drawLadder(L);
      // girders
      PLATFORMS.forEach((p, i) => drawGirder(p, i === 6));
      // goal beacon
      drawGoal(st);
      // KRANK the gearbot
      drawKrank(st);
      // barrels
      for (const b of st.barrels) drawBarrel(b);
      // player
      drawPlayer(st.player, st);

      // floating score texts
      ctx.font = "bold 12px 'Courier New', monospace";
      ctx.fillStyle = COLORS.goal;
      ctx.textAlign = "center";
      for (const t of st.texts) ctx.fillText(t.txt, t.x, t.y);

      drawHUD(st);
      drawOverlays(st);
    }

    function drawGirder(p, isGoal) {
      ctx.fillStyle = isGoal ? "#c94f7c" : COLORS.girder;
      ctx.fillRect(p.x, p.y, p.w, 12);
      ctx.fillStyle = isGoal ? "#7c2b4b" : COLORS.girderDark;
      for (let x = p.x + 4; x < p.x + p.w - 6; x += 16) {
        ctx.beginPath();
        ctx.moveTo(x, p.y + 10); ctx.lineTo(x + 8, p.y + 2);
        ctx.lineWidth = 3; ctx.strokeStyle = ctx.fillStyle; ctx.stroke();
      }
      ctx.fillRect(p.x, p.y + 10, p.w, 2);
    }

    function drawLadder(L) {
      ctx.strokeStyle = COLORS.ladder;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(L.x - 7, L.yTop); ctx.lineTo(L.x - 7, L.yBot);
      ctx.moveTo(L.x + 7, L.yTop); ctx.lineTo(L.x + 7, L.yBot);
      ctx.stroke();
      ctx.lineWidth = 2;
      for (let y = L.yTop + 6; y < L.yBot; y += 10) {
        ctx.beginPath(); ctx.moveTo(L.x - 7, y); ctx.lineTo(L.x + 7, y); ctx.stroke();
      }
    }

    function drawPlayer(P, st) {
      if (st.screen === "dead" && Math.floor(st.tick / 6) % 2 === 0) return; // blink
      const x = P.x, y = P.y;
      const stepAnim = Math.floor(P.anim / 6) % 2 === 0;
      // legs
      ctx.fillStyle = COLORS.playerSuit;
      if (P.climbing) {
        ctx.fillRect(x - 7, y - 8, 5, 8);
        ctx.fillRect(x + 2, y - (stepAnim ? 10 : 6), 5, stepAnim ? 10 : 6);
      } else {
        ctx.fillRect(x - (stepAnim ? 8 : 5), y - 8, 5, 8);
        ctx.fillRect(x + (stepAnim ? 3 : 0), y - 8, 5, 8);
      }
      // body
      ctx.fillRect(x - 7, y - 17, 14, 10);
      // head
      ctx.fillStyle = "#ffd9a8";
      ctx.fillRect(x - 5, y - 25, 10, 8);
      // hard hat
      ctx.fillStyle = COLORS.player;
      ctx.fillRect(x - 7, y - 28, 14, 4);
      ctx.fillRect(x - 4, y - 30, 8, 3);
      // eye
      ctx.fillStyle = "#1a1a2e";
      ctx.fillRect(x + (P.facing > 0 ? 1 : -3), y - 23, 2, 2);
    }

    function drawBarrel(b) {
      ctx.save();
      ctx.translate(b.x, b.y - 7);
      ctx.rotate(b.roll / 8);
      ctx.fillStyle = COLORS.barrel;
      ctx.beginPath(); ctx.arc(0, 0, 7, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = COLORS.barrelDark;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(0, 0, 6, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(-6, 0); ctx.lineTo(6, 0); ctx.stroke();
      ctx.restore();
    }

    function drawKrank(st) {
      const bounce = st.krankAnim > 10 ? -4 : 0;
      const x = 30, y = 120 + bounce;
      ctx.fillStyle = COLORS.krank;
      ctx.fillRect(x - 16, y - 34, 32, 26); // body
      ctx.fillRect(x - 22, y - 26, 6, 14);  // arm L
      ctx.fillRect(x + 16, y - 26, 6, 14);  // arm R
      ctx.fillStyle = "#2a1b4d";
      ctx.fillRect(x - 10, y - 28, 20, 8);  // visor
      ctx.fillStyle = st.krankAnim > 10 ? "#ff4d6d" : "#7dff8a";
      ctx.fillRect(x - 6 + (Math.floor(st.tick / 20) % 3) * 4, y - 26, 4, 4); // eye
      ctx.fillStyle = COLORS.krank;
      ctx.fillRect(x - 12, y - 8, 8, 8); // treads
      ctx.fillRect(x + 4, y - 8, 8, 8);
    }

    function drawGoal(st) {
      const g = PLATFORMS[6];
      const cx = g.x + g.w / 2, cy = g.y - 14;
      const pulse = 3 + Math.sin(st.tick / 12) * 2;
      ctx.fillStyle = "rgba(125,255,138,0.15)";
      ctx.beginPath(); ctx.arc(cx, cy, 14 + pulse, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = COLORS.goal;
      ctx.fillRect(cx - 6, cy - 10, 12, 20);
      ctx.fillStyle = "#0f2e14";
      ctx.fillRect(cx - 3, cy - 6, 6, 12);
      ctx.font = "bold 9px 'Courier New', monospace";
      ctx.fillStyle = COLORS.goal;
      ctx.textAlign = "center";
      ctx.fillText("POWER CELL", cx, g.y - 34);
    }

    function drawHUD(st) {
      ctx.font = "bold 13px 'Courier New', monospace";
      ctx.textAlign = "left";
      ctx.fillStyle = COLORS.text;
      ctx.fillText(`SCORE ${String(st.score).padStart(6, "0")}`, 8, 20);
      ctx.textAlign = "right";
      ctx.fillText(`HI ${String(Math.max(st.hi, st.score)).padStart(6, "0")}`, GW - 8, 20);
      ctx.textAlign = "left";
      ctx.fillText(`LV ${st.level}`, 8, 38);
      for (let i = 0; i < st.lives; i++) {
        ctx.fillStyle = COLORS.player;
        ctx.fillRect(60 + i * 16, 29, 10, 10);
      }
    }

    function drawOverlays(st) {
      if (st.screen === "playing" || st.screen === "dead") return;
      ctx.fillStyle = "rgba(10,6,26,0.82)";
      ctx.fillRect(0, 0, GW, GH);
      ctx.textAlign = "center";
      ctx.fillStyle = COLORS.text;

      if (st.screen === "title") {
        ctx.font = "bold 30px 'Courier New', monospace";
        ctx.fillStyle = COLORS.girder;
        ctx.fillText("GIRDER", GW / 2, 200);
        ctx.fillText("PANIC", GW / 2, 235);
        ctx.font = "bold 12px 'Courier New', monospace";
        ctx.fillStyle = COLORS.text;
        ctx.fillText("KRANK stole the power cell!", GW / 2, 280);
        ctx.fillText("Climb the girders. Dodge the barrels.", GW / 2, 300);
        if (Math.floor(st.tick / 30) % 2 === 0) {
          ctx.fillStyle = COLORS.goal;
          ctx.fillText("PRESS JUMP TO START", GW / 2, 350);
        }
        ctx.fillStyle = "rgba(253,246,227,0.6)";
        ctx.font = "11px 'Courier New', monospace";
        ctx.fillText("Keyboard: arrows / WASD + space", GW / 2, 390);
      } else if (st.screen === "levelup") {
        ctx.font = "bold 22px 'Courier New', monospace";
        ctx.fillStyle = COLORS.goal;
        ctx.fillText("CELL RECOVERED!", GW / 2, 250);
        ctx.font = "bold 14px 'Courier New', monospace";
        ctx.fillStyle = COLORS.text;
        ctx.fillText(`GET READY — LEVEL ${st.level + 1}`, GW / 2, 290);
      } else if (st.screen === "gameover") {
        ctx.font = "bold 26px 'Courier New', monospace";
        ctx.fillStyle = COLORS.player;
        ctx.fillText("GAME OVER", GW / 2, 240);
        ctx.font = "bold 14px 'Courier New', monospace";
        ctx.fillStyle = COLORS.text;
        ctx.fillText(`SCORE ${st.score}`, GW / 2, 280);
        if (Math.floor(st.tick / 30) % 2 === 0) {
          ctx.fillStyle = COLORS.goal;
          ctx.fillText("PRESS JUMP TO RETRY", GW / 2, 330);
        }
      }
    }

    const loop = () => { step(); draw(); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
    };
  }, []);

  // ---------- touch controls ----------
  const bind = (key) => ({
    onPointerDown: (e) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); keys.current[key] = true; },
    onPointerUp: () => { keys.current[key] = false; },
    onPointerCancel: () => { keys.current[key] = false; },
    onPointerLeave: () => { keys.current[key] = false; },
    onContextMenu: (e) => e.preventDefault(),
  });

  const btn = {
    userSelect: "none", WebkitUserSelect: "none", touchAction: "none",
    display: "flex", alignItems: "center", justifyContent: "center",
    borderRadius: 12, border: "2px solid #3d3560",
    background: "#1f1840", color: "#fdf6e3",
    fontFamily: "'Courier New', monospace", fontWeight: 700,
    boxShadow: "0 3px 0 #0c0920", cursor: "pointer",
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#0a0619",
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "8px 8px 16px", fontFamily: "'Courier New', monospace",
    }}>
      <div style={{ position: "relative", width: "100%", maxWidth: 400 }}>
        <canvas
          ref={canvasRef}
          width={GW}
          height={GH}
          style={{
            width: "100%", display: "block", borderRadius: 8,
            border: "3px solid #3d3560", imageRendering: "pixelated",
            touchAction: "none",
          }}
        />
        {/* CRT scanlines */}
        <div style={{
          position: "absolute", inset: 3, pointerEvents: "none", borderRadius: 6,
          background: "repeating-linear-gradient(0deg, rgba(0,0,0,0.14) 0px, rgba(0,0,0,0.14) 1px, transparent 2px, transparent 4px)",
        }} />
      </div>

      {/* controls */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        width: "100%", maxWidth: 400, marginTop: 12, gap: 10,
      }}>
        <div style={{ display: "flex", gap: 8 }}>
          <div {...bind("left")} style={{ ...btn, width: 62, height: 62, fontSize: 24 }}>◀</div>
          <div {...bind("right")} style={{ ...btn, width: 62, height: 62, fontSize: 24 }}>▶</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div {...bind("up")} style={{ ...btn, width: 56, height: 42, fontSize: 20 }}>▲</div>
          <div {...bind("down")} style={{ ...btn, width: 56, height: 42, fontSize: 20 }}>▼</div>
        </div>
        <div {...bind("jump")} style={{
          ...btn, width: 82, height: 82, borderRadius: "50%", fontSize: 14,
          background: "#5a1f2e", border: "2px solid #a8455c",
        }}>JUMP</div>
      </div>
    </div>
  );
}
